import { ReactElement, useState } from 'react'
import { Formik } from 'formik'
import { keys, pickBy, isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { Column } from '@10xLabs/react-web/components/Column'
import { Row } from '@10xLabs/react-web/components/Row'
import { Tag } from 'components/v2/Tag'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { FilterHeader, FilterWrapper, FilterToggle, FiltersWrapper, TagsWrapper } from 'components/Filters'
import { ESubcampaignsFilters, IFilterPlace, ISubcampaignsFilters, ISubcampaignsFiltersProps } from './types'

const messages = defineMessages({
  selectOrigin: {
    id: 'subcampaings.filters.selectOrigin',
    defaultMessage: 'Select origin'
  },
  selectDestination: {
    id: 'subcampaings.filters.selectDestination',
    defaultMessage: 'Select destination'
  }
})

export const SubcampaignFilters = ({ filters }: ISubcampaignsFiltersProps): ReactElement => {
  const [, setSearch] = useSearchParams()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const applyFilters = (values: ISubcampaignsFilters): void => {
    const searchFilters = pickBy(
      {
        origin: values.origin?.id,
        destination: values.destination?.id
      },
      (value) => {
        return !isEmpty(value)
      }
    )

    setSearch(searchFilters as Record<string, string>)
  }

  const initialValues = {
    origin: filters?.origin || {
      id: '',
      name: ''
    },
    destination: filters?.destination || {
      id: '',
      name: ''
    }
  }

  return (
    <Formik initialValues={initialValues} onSubmit={applyFilters} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, setFieldValue, values } = props

        const handleFilterDelete = (id: string): void => {
          setFieldValue(id, '')
          applyFilters({
            ...values,
            [id]: {
              id: '',
              name: ''
            }
          })
        }
        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <FilterToggle onClick={toggleFilters} />
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      const value = filters[filterKey as keyof typeof filters]
                      if (filterKey === ESubcampaignsFilters.Origin && value.name) {
                        return (
                          <Tag
                            key={filterKey}
                            onDelete={(): void => {
                              handleFilterDelete(filterKey)
                            }}
                          >
                            <FormattedMessage id="dictionary.origin" defaultMessage="Origin" />: {value.name}
                          </Tag>
                        )
                      }
                      if (filterKey === ESubcampaignsFilters.Destination && value.name) {
                        return (
                          <Tag
                            key={filterKey}
                            onDelete={(): void => {
                              handleFilterDelete(filterKey)
                            }}
                          >
                            <FormattedMessage id="dictionary.code" defaultMessage="Destination" />: {value.name}
                          </Tag>
                        )
                      }

                      return null
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <Row>
                  <Column width={`${100 / 3}%`}>
                    <PlaceAutocomplete
                      placeholder={intl.formatMessage(messages.selectOrigin)}
                      value={values.origin.name}
                      label={<FormattedMessage id="dictionary.origin" defaultMessage="Origin" />}
                      onChange={(place: IFilterPlace): void => {
                        setFieldValue('origin', place)
                        applyFilters({
                          ...values,
                          origin: place
                        })
                      }}
                    />
                  </Column>
                  <Column width={`${100 / 3}%`}>
                    <PlaceAutocomplete
                      placeholder={intl.formatMessage(messages.selectDestination)}
                      value={values.destination.name}
                      label={<FormattedMessage id="dictionary.destination" defaultMessage="Destination" />}
                      onChange={(place: IFilterPlace): void => {
                        setFieldValue('destination', place)
                        applyFilters({
                          ...values,
                          destination: place
                        })
                      }}
                    />
                  </Column>
                </Row>
              </FiltersWrapper>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}
